<template>
  <div class="h-100">
    <div class="d-flex justify-content-between align-items-center">
      <h5
        class="m-0 text-truncate"
        :title="title"
        v-html="title"
      />

      <div
        class="d-flex justify-content-end"
        v-if="downloadEnabled || extraActions.length > 0"
      >
        <v-chart-dropdown
          :download-enabled="true"
          :name="name"
          :extra-actions="extraActions"
          @extra-action-clicked="(actionName) => $emit(actionName)"
        />
      </div>
    </div>

    <canvas
      ref="chart"
      :class="[`chart-${size}`]"
    />

    <!-- Invisible chart for downloading -->
    <canvas
      ref="chartDownload"
      class="chart-download"
    ></canvas>
  </div>
</template>

<script>
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart,
  registerables,
} from 'chart.js';
import {
  DEFAULT_CHART_COLORS,
  defaultChartOptions,
  defaultChartDataLabelConfig,
  defaultDataLabels,
} from '@/organisation/services/charts';
import ChartDropdown from '@/organisation/components/dashboard/ChartDropdown.vue';

export default {
  name: 'Chart',
  components: {
    'v-chart-dropdown': ChartDropdown,
  },
  props: {
    extraActions: {
      // Example: [{ text: 'word.show', name: 'show', 'icon': 'eye' }]
      type: Array,
      default() {
        return [];
      },
    },
    actionText: {
      type: String,
      default() {
        return null;
      },
    },
    asPercentageDataLabels: {
      type: Boolean,
      default() {
        return false;
      },
    },
    chartType: {
      type: String,
      required: true,
    },
    colors: {
      type: Array,
      default() {
        return DEFAULT_CHART_COLORS;
      },
    },
    swapColors: {
      type: Boolean,
      default() {
        return false;
      },
    },
    dataSets: {
      required: true,
      type: Array,
    },
    downloadEnabled: {
      type: Boolean,
      default() {
        return true;
      },
    },
    labels: {
      required: true,
      type: Array,
    },
    name: {
      default() {
        return 'chart';
      },
    },
    showLegend: {
      required: false,
      default() {
        return null;
      },
    },
    size: {
      type: String,
      default() {
        return 'lg';
      },
    },
    small: {
      type: Boolean,
      default() {
        return false;
      },
    },
    title: {
      default() {
        return null;
      },
    },
  },
  data() {
    return {
      chart: null,
      copied: false,
    };
  },
  methods: {
    async createBarChart(chartConfig) {
      Chart.register(...registerables);
      const dataSets = this.dataSets.map((dataSet, index) => {
        let colors;
        if (this.swapColors) {
          colors = this.colors.map((color) => (color.backgroundColor));
        } else {
          colors = [
            this.colors[index].backgroundColor,
          ];
        }
        return {
          label: dataSet.label,
          data: dataSet.data,
          backgroundColor: colors,
          datalabels: defaultDataLabels({ size: 'sm' }),
        };
      });

      // eslint-disable-next-line no-new
      this.chart = new Chart(
        this.$refs.chart.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          plugins: [
            ChartDataLabels,
          ],
          options: defaultChartOptions(
            null,
            this.getShowLegend(),
            true,
            chartConfig,
            true,
          ),
        },
      );

      await this.createDownloadableChart(
        'bar',
        dataSets,
        chartConfig,
      );
    },
    async createDoughnutChart(chartConfig) {
      Chart.register(...registerables);

      const dataSets = this.dataSets.map((dataSet, index) => ({
        label: dataSet.label,
        data: dataSet.data,
        backgroundColor: dataSet.backgroundColor,
        borderColor: dataSet.borderColor,
        borderWidth: this.colors[index].borderWidth,
        datalabels: defaultDataLabels({ size: 'sm' }),
      }));

      // eslint-disable-next-line no-new
      this.chart = new Chart(
        this.$refs.chart.getContext('2d'),
        {
          type: 'doughnut',
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          plugins: [
            ChartDataLabels,
          ],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  label(context) {
                    return `${context.label} (${context.parsed})`;
                  },
                },
              },
              title: {
                display: false,
                text: this.title,
                align: 'start',
                padding: {
                  top: 5,
                  bottom: 30,
                },
                font: {
                  size: 13,
                  weight: 'bold',
                },
              },
              legend: {
                display: true,
                position: 'bottom',
                labels: {
                  boxWidth: 12,
                },
              },
              datalabels: defaultChartDataLabelConfig(
                this.size !== 'lg',
                chartConfig,
              ),
            },
          },
        },
      );

      await this.createDownloadableChart(
        'doughnut',
        dataSets,
        chartConfig,
      );
    },
    async createDownloadableChart(chartType, dataSets, chartConfig) {
      // eslint-disable-next-line no-new
      new Chart(
        this.$refs.chartDownload.getContext('2d'),
        {
          type: chartType,
          plugins: [
            ChartDataLabels,
          ],
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          options: {
            ...defaultChartOptions(
              this.title,
              this.getShowLegend(),
              true,
              chartConfig,
              this.chartType !== 'doughnut',
            ),
          },
        },
      );
    },
    async createTimeSeriesChart(chartConfig) {
      // A chart that is plotted on a time based x-axis;
      Chart.defaults.font.size = 11;

      Chart.register(...registerables);
      const dataSets = this.dataSets.map((dataSet, index) => ({
        type: dataSet.type,
        label: dataSet.label,
        data: dataSet.values,
        backgroundColor: [
          this.colors[index].backgroundColor,
        ],
        borderColor: [
          this.colors[index].borderColor,
        ],
        borderDash: this.colors[index].borderDash,
        borderWidth: this.colors[index].borderWidth,
        datalabels: dataSet.datalabels,
      }));

      // eslint-disable-next-line no-new
      this.chart = new Chart(
        this.$refs.chart.getContext('2d'),
        {
          plugins: [
            ChartDataLabels,
          ],
          data: {
            labels: this.labels,
            datasets: dataSets,
          },
          options: defaultChartOptions(
            null,
            this.getShowLegend(),
            false,
            chartConfig,
            true,
          ),
        },
      );

      await this.createDownloadableChart(
        null,
        dataSets,
        chartConfig,
      );
    },
    getShowLegend() {
      if (this.showLegend === null) {
        return this.dataSets.length > 1;
      }
      return this.showLegend;
    },
  },
  mounted() {
    // Generate chart config for all chart types;
    const chartConfig = {
      asPercentageDataLabels: this.asPercentageDataLabels,
      size: this.size,
    };

    if (this.chartType === 'time-series') {
      this.createTimeSeriesChart(chartConfig);
    } else if (this.chartType === 'bar') {
      this.createBarChart(chartConfig);
    } else if (this.chartType === 'doughnut') {
      this.createDoughnutChart(chartConfig);
    } else {
      throw Error(`Chart type "${this.chartType}" not implemented.`);
    }
  },
};
</script>

<style scoped>
canvas.chart-lg {
  max-height: 302px;
  min-height: 300px;
}

canvas.chart-md {
  max-height: 152px;
  min-height: 150px;
}

canvas.chart-sm {
  height: 100px;
  max-height: 100px;
}
</style>
